/**
 * TenantInfoDropdown - Displays tenant information in a dropdown
 * Replaces legacy InfoDropDownContainer from _HeaderPartial.cshtml
 */

import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui';
import { ChevronDown } from 'lucide-react';
import {
  useTenant,
  useBilling,
  useNetworkOwnerType,
} from '../UserContextProvider/UserContextProvider.store';
import { useHasAnyRole } from '../UserContextProvider/use-has-role';

export function TenantInfoDropdown() {
  const { t } = useTranslation(NS.COMMON);
  const tenant = useTenant();
  const billing = useBilling();
  const networkOwnerType = useNetworkOwnerType();

  // Check if user has admin roles
  const isAdmin = useHasAnyRole('Root', 'NetOps', 'TechOps');

  // Show billing only for network 50 with admin role
  const showBilling = isAdmin && tenant?.networkId === 50 && billing;

  if (!tenant) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
        <img
          src={tenant.headerLogo}
          alt={tenant.name}
          className="h-12 w-12 rounded object-contain"
        />
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="min-w-[300px] p-4">
        <div className="space-y-2 text-sm">
          {/* Tenant Name */}
          <div className="font-medium text-foreground">
            {tenant.name}
          </div>

          {/* Network & Mailbox IDs */}
          <div className="space-y-1 text-muted-foreground">
            <div className="pl-4">
              {t('TenantInfo_NetworkId', { defaultValue: 'Network ID' })}: {tenant.networkId}
            </div>
            <div className="pl-4">
              {t('TenantInfo_MailboxId', { defaultValue: 'Mailbox ID' })}: {tenant.mailboxId}
            </div>

            {/* Network Type (admin only) */}
            {isAdmin && networkOwnerType && (
              <div className="pl-4">
                {t('TenantInfo_NetworkType', { defaultValue: 'Network Type' })}: {networkOwnerType}
              </div>
            )}
          </div>

          {/* Billing Information (admin + network 50 only) */}
          {showBilling && (
            <div className="mt-3 pt-3 border-t space-y-1">
              {billing.billingType === 'Paid' && (
                <>
                  <div className="pl-4 font-medium">
                    {t('TenantInfo_StripeInfo', { defaultValue: 'Stripe Info' })}:
                  </div>
                  <div className="pl-8 text-muted-foreground space-y-1">
                    <div>{t('TenantInfo_CustomerId', { defaultValue: 'Customer ID' })}: {billing.customerId}</div>
                    <div>{t('TenantInfo_ExpireDate', { defaultValue: 'Expire Date' })}: {billing.expireDate || 'N/A'}</div>
                    {billing.subscriptionId && (
                      <div>{t('TenantInfo_SubscriptionId', { defaultValue: 'Subscription ID' })}: {billing.subscriptionId}</div>
                    )}
                    {billing.planId && (
                      <div>{t('TenantInfo_PlanId', { defaultValue: 'Plan ID' })}: {billing.planId}</div>
                    )}
                    {billing.productId && (
                      <div>{t('TenantInfo_ProductId', { defaultValue: 'Product ID' })}: {billing.productId}</div>
                    )}
                    {billing.ediId && (
                      <div>{t('TenantInfo_EdiId', { defaultValue: 'EDI ID' })}: {billing.ediId}</div>
                    )}
                  </div>
                </>
              )}

              {billing.billingType === 'Trial' && (
                <>
                  <div className="pl-4">
                    {t('TenantInfo_BillingInfo', { defaultValue: 'Billing Info' })}: {t('TenantInfo_Trial', { defaultValue: 'Trial' })}
                  </div>
                  <div className="pl-4">
                    {t('TenantInfo_TrialEndDate', { defaultValue: 'Trial End Date' })}: {billing.expireDate || 'N/A'}
                  </div>
                </>
              )}

              {billing.billingType === 'Exempt' && (
                <div className="pl-4">
                  {t('TenantInfo_BillingInfo', { defaultValue: 'Billing Info' })}: {t('TenantInfo_Exempt', { defaultValue: 'Exempt' })}
                </div>
              )}
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
